import { useCallback, useMemo, useRef, useState } from 'react';
import type { CoachCardSource } from '../../app/coach';
import { nameJerseyRefs } from '../../domain/teamProfile';
import type { AdjustmentEntry, CoachCardRow, MatchPurpose, OurPlayerRow } from '../../types';
import { PhaseIcon } from '../PhaseIcon';
import { phaseLabelB } from '../phases';
import { cardPagesB, framingForB, pageFromScrollB } from './cardB';

/**
 * B-16 — **the card** (design spec §2.5 step 4; scenarios 29–32): one adjustment at a time, each
 * with the phase it applies to and the observations that led to it. The thing the coach actually
 * reads out, so every decision below is about a person holding a phone in front of a team.
 *
 * ── One page, one thing to say ───────────────────────────────────────────────
 *
 * Scenario 29 is *"one adjustment at a time (paged)"*, and the reason is the reading, not the
 * layout: a list of eight points is something to skim, a page is something to say. So the page
 * carries exactly three things in the order they are spoken — **the phase** it applies to (when in
 * the game this matters), **the adjustment** (what we are doing about it, in the largest type on
 * the surface), and **"because —"** the patterns behind it with their counts and any annotations
 * (why we think so, when a player asks). Nothing else competes.
 *
 * ── The pager is a scroll-snap strip with buttons, not a swipe-only deck ─────
 *
 * Scroll-snap is floor-safe (skill §8) and indicators sit below the content they page (skill §7),
 * so a swipe works exactly as the platform's does. Prev/Next exist beside the dots anyway, for
 * three reasons: a card may hold up to `COACH_CARD_MAX_ENTRIES` pages and twenty dots are an
 * indicator, never a target; a horizontal swipe that starts near the screen edge competes with the
 * system's own gesture; and a coach mid-huddle should not have to be accurate. The dots are
 * `aria-hidden` decoration — "Page 3 of 8" is the accessible statement, and it is live.
 *
 * **Scrolling is only ever caused by a press.** No effect moves the strip in response to state
 * (skill §7: a pager must never fight the finger) — the buttons scroll and set the page in the
 * same handler, and the strip's own `scroll` event is what a finger's swipe reports back through.
 *
 * ── What this component will not do ─────────────────────────────────────────
 *
 * It never writes. A finalized card is immutable (`ERR_CARD_FINALIZED`), and the surface that
 * renders it holds no control that could try — the negative is structural, not guarded. It also
 * renders a *draft* card unchanged (the full-time flow's halftime reference, §2.5), because
 * `rendersOnCard` already answers what a draft would put on a page.
 */

export interface CoachCardViewProps {
  /** The card row, draft or final. Rendered exactly as stored — this surface never edits. */
  readonly card: CoachCardRow;
  /** The match's purpose. With the card's own context it derives the framing (§2.5, scenario 32). */
  readonly purpose: MatchPurpose | null;
  /**
   * The patterns this card may cite. A superset is fine and expected (one read serves both of a
   * match's cards); each page renders only the ids its own entry names.
   */
  readonly sources: readonly CoachCardSource[];
  /**
   * §18.3/§18.4 — the roster the **name join** reads. A `#7` in the model's own words renders as
   * `#7 Maya` here and **only** here: `nameJerseyRefs` is never applied to the stored text, so
   * §12's existing negative — *"a finalized card's text never changes afterward"* — holds unchanged.
   *
   * Optional and defaulting to none, because a card can be rendered by a surface with no roster in
   * hand (the match file); rendering `#7` bare is the honest fallback and never a wrong name.
   */
  readonly roster?: readonly OurPlayerRow[];
  /** Optional "when" line — the match date on a file spanning seasons. */
  readonly meta?: string;
  /** Probe/test id root. Distinct per card, because a file renders several at once. */
  readonly testId: string;
}

export function CoachCardView({
  card,
  purpose,
  sources,
  roster,
  meta,
  testId,
}: CoachCardViewProps) {
  const framing = framingForB(purpose, card.context);
  const pages = useMemo(() => cardPagesB(card), [card]);
  const byId = useMemo(() => {
    const map = new Map<string, CoachCardSource>();
    for (const source of sources) map.set(source.id, source);
    return map;
  }, [sources]);

  const [page, setPage] = useState(0);
  const pager = useRef<HTMLUListElement | null>(null);

  /**
   * Page by press. **The scroll is instant, not smooth, and that is a fix rather than a taste.**
   *
   * A smooth `scrollTo` travels through the positions between two pages, and the strip fires a
   * `scroll` event at each one. Measured in WebKit (13 events across a two-page programmatic
   * scroll, reporting `0,1,1,1,1,1,1,2,2,…`): every one of those intermediate frames rounds to the
   * page the user is LEAVING, so the handler below spent the whole animation overruling the press
   * with the old page — the dot jumped back and the "page k of n" line lied for ~200 ms after every
   * tap. Landing the strip immediately means a scroll event can only ever confirm the intent.
   *
   * Nothing is lost: the movement a press needs to acknowledge is in the dot's width transition
   * (which honours `prefers-reduced-motion`), and a page change on a button press is supposed to be
   * instant (skill §2). A finger's own swipe is untouched — its intermediate positions ARE the
   * truth, and the handler follows them.
   */
  const goTo = useCallback(
    (index: number): void => {
      const strip = pager.current;
      if (strip === null) return;
      const target = Math.min(Math.max(index, 0), pages.length - 1);
      setPage(target);
      strip.scrollTo({ left: target * strip.clientWidth, behavior: 'auto' });
    },
    [pages.length],
  );

  return (
    <article
      className="cardb"
      data-testid={testId}
      data-framing={framing?.key ?? 'none'}
      data-context={card.context}
      data-status={card.status}
      data-pages={String(pages.length)}
      data-page={String(page)}
      aria-label={framing?.title ?? 'Unnamed card'}
    >
      <header className="cardb__top">
        {framing === null ? (
          /* Scenario 32's "no fourth exists", rendered honestly: an unframeable pair gets no
             invented name — and the card's contents are still shown in full, because the entries
             are the user's work and a naming problem is no reason to hide them. The copy is true
             of BOTH null arms (a purposeless match, and the advance-scout break the engine
             refuses) — naming only one of them would be a sentence that lies half the time. */
          <p className="cardb__unnamed" data-testid={`${testId}-unnamed`}>
            Unnamed card — a card is named by the match’s purpose and the moment it was made, and
            this pair makes none of the three.
          </p>
        ) : (
          <>
            <h3 className="cardb__title">{framing.title}</h3>
            <p className="cardb__lead">{framing.lead}</p>
          </>
        )}
        <p className="cardb__meta" data-testid={`${testId}-meta`}>
          {meta !== undefined && <span className="cardb__when">{meta}</span>}
          <span className="cardb__count">
            {pages.length} {pages.length === 1 ? 'adjustment' : 'adjustments'}
          </span>
        </p>
      </header>

      {pages.length === 0 ? (
        <p className="cardb__empty" data-testid={`${testId}-empty`}>
          Nothing on this card yet.
        </p>
      ) : (
        <>
          <ul
            className="cardb-strip"
            data-testid={`${testId}-strip`}
            ref={pager}
            tabIndex={0}
            aria-label="Adjustments, one per page"
            onScroll={(event) => {
              const strip = event.currentTarget;
              setPage(pageFromScrollB(strip.scrollLeft, strip.clientWidth, pages.length));
            }}
          >
            {pages.map((entry, index) => (
              <li
                className="cardb-page"
                key={entry.entryId}
                data-testid={`${testId}-page-${String(index)}`}
                data-entry={entry.entryId}
                data-phase={entry.phase}
                aria-label={`Adjustment ${String(index + 1)} of ${String(pages.length)}`}
              >
                <p className="cardb-page__phase">
                  <PhaseIcon phase={entry.phase} />
                  <span className="cardb-page__phaselabel">{phaseLabelB(entry.phase)}</span>
                </p>
                <p className="cardb-page__text">{nameJerseyRefs(entry.text, roster ?? [])}</p>
                <Because entry={entry} byId={byId} testId={`${testId}-because-${String(index)}`} />
              </li>
            ))}
          </ul>

          {/* Below the content it pages — the platform-wide convention (skill §7). */}
          <nav className="cardb-nav" aria-label="Card pages">
            <button
              type="button"
              className="cardb-nav__step"
              data-testid={`${testId}-prev`}
              disabled={page === 0}
              aria-label="Previous adjustment"
              onClick={() => {
                goTo(page - 1);
              }}
            >
              ‹
            </button>
            <ol className="cardb-dots" aria-hidden="true">
              {pages.map((entry, index) => (
                <li
                  className="cardb-dot"
                  key={entry.entryId}
                  data-current={index === page ? 'true' : 'false'}
                />
              ))}
            </ol>
            <button
              type="button"
              className="cardb-nav__step"
              data-testid={`${testId}-next`}
              disabled={page === pages.length - 1}
              aria-label="Next adjustment"
              onClick={() => {
                goTo(page + 1);
              }}
            >
              ›
            </button>
          </nav>
          <p className="cardb-nav__count" role="status" data-testid={`${testId}-position`}>
            Page {page + 1} of {pages.length}
          </p>
        </>
      )}
    </article>
  );
}

interface BecauseProps {
  readonly entry: AdjustmentEntry;
  readonly byId: ReadonlyMap<string, CoachCardSource>;
  readonly testId: string;
}

/**
 * "because —": the observations behind this adjustment (scenario 29), each with the count that is
 * its evidence and any annotation the user attached to it.
 *
 * Three states, and the two quiet ones are why this is a component rather than a conditional:
 * an entry that cites **nothing** is legitimate (a manual point, and a validated AI suggestion may
 * cite nothing either — §5.3 keeps it), while an entry whose cited ids **cannot be resolved** is a
 * different fact entirely and must not be worded as the first. `ERR_CARD_SOURCE` makes the second
 * unreachable through the app; it is answered rather than assumed because the alternative is a
 * "because —" heading with nothing under it on the one surface read aloud to a team.
 */
function Because({ entry, byId, testId }: BecauseProps) {
  const cited = entry.sourceHypothesisIds
    .map((id) => byId.get(id))
    .filter((source): source is CoachCardSource => source !== undefined);

  if (cited.length === 0) {
    return (
      <p className="cardb-because__none" data-testid={testId}>
        {entry.sourceHypothesisIds.length === 0
          ? 'No pattern cited.'
          : 'The patterns behind this are no longer on this match.'}
      </p>
    );
  }

  return (
    <div className="cardb-because" data-testid={testId}>
      <p className="cardb-because__label">because —</p>
      <ul className="cardb-because__list">
        {cited.map((source) => (
          <li className="cardb-src" key={source.id}>
            <span className="cardb-src__count" aria-label={`seen ${String(source.count)} times`}>
              {source.count}
            </span>
            <span className="cardb-src__body">
              <span className="cardb-src__text">{source.text}</span>
              {source.annotations.map((note, index) => (
                <span className="cardb-src__note" key={`${source.id}-${String(index)}`}>
                  {note}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
